import { useEffect } from "react";
import { apiService } from "../../api/apiService";
import { useAuth } from "../../hooks/useAuth";
import { zbudujMaske, uruchomMaskeDom, ZDARZENIE_TRYBU_NAGRANIA } from "../../utils/maskaNagrania";

/** Co ile sprawdzać tryb, gdy karta jest WIDOCZNA. Ukryta karta nie pyta wcale. */
export const OKRES_SPRAWDZANIA_MS = 60 * 1000;
/**
 * Najkrótszy odstęp między sprawdzeniami z powrotu do karty i z zegara. `focus` i `visibilitychange` przychodzą razem
 * przy powrocie do karty, a `focus` okna wraca też po każdym kliknięciu z ramki podglądu z powrotem na stronę.
 * Start i zdarzenie z suwaka administratora (`ZDARZENIE_TRYBU_NAGRANIA`) sprawdzają ZAWSZE.
 */
export const ODSTEP_SPRAWDZEN_MS = 5 * 1000;
/** Blokada przeładowania: najwyżej jedno automatyczne przeładowanie na kartę w tym oknie czasu (sessionStorage przeżywa F5). */
export const KLUCZ_PRZELADOWANIA = "maska-nagrania-przeladowanie";
export const BLOKADA_PRZELADOWANIA_MS = 2 * 60 * 1000;

const kartaWidoczna = () => typeof document === "undefined" || document.visibilityState !== "hidden";

/** Czy wolno przeładować teraz — i od razu zapisuje, że przeładowanie idzie (bez pętli także po F5). */
const przeladowanieDozwolone = () => {
    try {
        const ostatnie = Number(window.sessionStorage.getItem(KLUCZ_PRZELADOWANIA) || 0);
        if (ostatnie && Date.now() - ostatnie >= 0 && Date.now() - ostatnie < BLOKADA_PRZELADOWANIA_MS) return false;
        window.sessionStorage.setItem(KLUCZ_PRZELADOWANIA, String(Date.now()));
    } catch {
        /* pamięć przeglądarki zablokowana — zostaje blokada na czas życia tej strony (`przeladowano`) */
    }
    return true;
};

/** Podpis opcji słownika, od których zależy wygląd maski (inicjały, telefony, stopki). */
const podpisOpcji = (d) => JSON.stringify([d?.inicjaly ?? null, d?.maskujTelefony !== false, d?.maskujStopki !== false]);

/**
 * Włącza maskę nagrania WYŁĄCZNIE osobie, którą administrator wskazał w /admin/integrations
 * (karta „Monitoring aktywności", suwak „Wersja anonimowa"). Dla wszystkich innych to małe pytanie
 * o tryb: przy starcie sesji, po powrocie do karty i co minutę, gdy karta jest widoczna.
 *
 * [T2, właściciel 2026-09-14] „Wyłączyłem tryb anonimowy a dalej …”. Tryb był sprawdzany RAZ na konto, więc wyłączenie
 * w /admin nie zdejmowało maski do F5. Teraz tryb jest sprawdzany ponownie:
 *  • po `focus` okna i po `visibilitychange` do widocznej karty;
 *  • co `OKRES_SPRAWDZANIA_MS`, WYŁĄCZNIE gdy karta jest widoczna (zegar staje, gdy karta się chowa);
 *  • od razu po zdarzeniu `ZDARZENIE_TRYBU_NAGRANIA` — suwak wysyła je po udanym zapisie (administrator testujący na sobie).
 * Jedno zapytanie naraz: wyzwolenie w trakcie dołącza do trwającego, a zdarzenie z suwaka dokłada jedno sprawdzenie po nim
 * (trwające mogło wyjść przed zapisem). Błąd sieci zostawia stan, jaki jest.
 *  • WŁĄCZENIE w trakcie sesji: słownik i maska startują bez przeładowania.
 *  • WYŁĄCZENIE: zatrzymanie przywraca ekran i zwraca raport (`uruchomMaskeDom`). Ślad maski, którego przywrócenie nie
 *    cofnęło (`pelne: false`), albo wyjątek przy zatrzymaniu = JEDNO `window.location.reload()` — tylko gdy maska
 *    naprawdę stała na ekranie, najwyżej raz na `BLOKADA_PRZELADOWANIA_MS` w tej karcie.
 *  • Zdarzenie z suwaka przy włączonej masce pobiera słownik jeszcze raz: zmiana inicjałów albo opcji (telefony, stopki)
 *    przestawia maskę od razu w tej karcie. U innych osób — po ich przeładowaniu (`moj-tryb` niesie tylko „włączony”).
 * Maska dotyka wyłącznie wyświetlania — wartości pól i stan Reacta zostają prawdziwe (patrz utils/maskaNagrania.js).
 *
 * Zależność efektu = identyfikator konta, nie obiekt konta (AuthContext ustawia `user` dwa razy na
 * wejście — obiekt jest nowy, treść ta sama; patrz Layout.js „TOŻSAMOŚĆ KONTA").
 * Błąd sieci nie może niczego zepsuć: maska jest wygodą nagrania, aplikacja działa bez niej.
 */
const MaskaNagrania = () => {
    const { user } = useAuth();
    const idKonta = user?.id ?? null;

    useEffect(() => {
        if (!idKonta) return undefined;
        let zywy = true;
        let zatrzymaj = null;          // funkcja zatrzymująca maskę — jest, dopóki maska stoi na ekranie
        let podpis = null;             // podpis opcji słownika, z którym maska wystartowała
        let wToku = false;
        let jeszczeRaz = false;
        let ostatnieSprawdzenie = null;
        let przeladowano = false;
        let zegar = null;

        const zdejmijMaske = (poWylaczeniu) => {
            const stop = zatrzymaj;
            zatrzymaj = null;
            podpis = null;
            if (!stop) return;
            let raport;
            try {
                raport = stop();
            } catch {
                raport = { pelne: false };
            }
            if (!poWylaczeniu || !raport || raport.pelne !== false || przeladowano) return;
            if (!przeladowanieDozwolone()) return;
            przeladowano = true;
            window.location.reload();
        };

        const nalozMaske = (dane) => {
            zatrzymaj = uruchomMaskeDom(zbudujMaske(dane));
            podpis = podpisOpcji(dane);
        };

        const sprawdz = async (odswiezSlownik) => {
            const tryb = await apiService.getMojTrybNagrania();
            if (!zywy) return;
            const wlaczony = tryb?.data?.wlaczony;
            if (wlaczony === false) {
                zdejmijMaske(true);
                return;
            }
            // Odpowiedź bez jawnego `wlaczony` (strona zastępcza proxy albo index.html w trakcie wdrożenia) = jak błąd sieci:
            // stan zostaje. Tylko jawne `false` zdejmuje maskę w trakcie nagrania.
            if (wlaczony !== true) return;
            if (zatrzymaj && !odswiezSlownik) return;
            const slownik = await apiService.getSlownikNagrania();
            if (!zywy) return;
            const dane = slownik?.data;
            if (zatrzymaj) {
                if (podpisOpcji(dane) === podpis) return;
                zdejmijMaske(false);   // przestawienie opcji: bez przeładowania, nowa maska przykrywa ekran od razu
            }
            nalozMaske(dane);
        };

        const sprawdzTryb = async (wymus) => {
            if (!zywy) return;
            if (wToku) {
                if (wymus) jeszczeRaz = true;
                return;
            }
            if (!wymus && ostatnieSprawdzenie !== null && Date.now() - ostatnieSprawdzenie < ODSTEP_SPRAWDZEN_MS) return;
            wToku = true;
            let odswiezSlownik = !!wymus;
            try {
                do {
                    jeszczeRaz = false;
                    ostatnieSprawdzenie = Date.now();
                    try {
                        await sprawdz(odswiezSlownik);
                    } catch {
                        /* błąd sieci: maska zostaje taka, jaka jest — następna okazja sprawdzi jeszcze raz */
                    }
                    odswiezSlownik = true;   // powtórka jest zawsze skutkiem zdarzenia z suwaka
                } while (jeszczeRaz && zywy);
            } finally {
                wToku = false;
            }
        };

        const uruchomZegar = () => {
            if (zegar !== null) return;
            zegar = window.setInterval(() => { if (kartaWidoczna()) sprawdzTryb(false); }, OKRES_SPRAWDZANIA_MS);
        };
        const zatrzymajZegar = () => {
            if (zegar === null) return;
            window.clearInterval(zegar);
            zegar = null;
        };
        const naWidocznosc = () => {
            if (!kartaWidoczna()) { zatrzymajZegar(); return; }
            uruchomZegar();
            sprawdzTryb(false);
        };
        const naFokus = () => sprawdzTryb(false);
        const naZmianeTrybu = () => sprawdzTryb(true);

        // Start sprawdza zawsze (także w karcie otwartej w tle). Słownik idzie wyłącznie przy włączonym trybie.
        sprawdzTryb(true);
        if (kartaWidoczna()) uruchomZegar();
        document.addEventListener("visibilitychange", naWidocznosc);
        window.addEventListener("focus", naFokus);
        window.addEventListener(ZDARZENIE_TRYBU_NAGRANIA, naZmianeTrybu);

        return () => {
            zywy = false;
            zatrzymajZegar();
            document.removeEventListener("visibilitychange", naWidocznosc);
            window.removeEventListener("focus", naFokus);
            window.removeEventListener(ZDARZENIE_TRYBU_NAGRANIA, naZmianeTrybu);
            zdejmijMaske(false);
        };
    }, [idKonta]);

    return null;
};

export default MaskaNagrania;
